import { keepPreviousData, useInfiniteQuery, useMutation, useQuery, useQueryClient, type InfiniteData, type QueryClient } from "@tanstack/react-query";

import { del, get, patch, post, upload } from "./api";
import { currentSurface } from "./host";
import type { AuditEntry, CardFields, Pass, PassStatus, ScanHistory, ScannerSettings, Stats, Tier, TierColors, Venue } from "./types";

/**
 * One place for query keys, fetchers and mutations. Every mutation invalidates
 * exactly the keys it changes, so a screen never shows a card the door already
 * refuses. Lists poll in the background: managers leave the panel open all night.
 */

export interface PassFilters {
  q?: string;
  status?: string;
  tierId?: string;
}

export const keys = {
  venue: ["venue"] as const,
  passes: (f: PassFilters = {}) => ["passes", { q: f.q ?? "", status: f.status ?? "", tierId: f.tierId ?? "" }] as const,
  pass: (serial: string) => ["pass", serial] as const,
  history: (serial: string) => ["pass-history", serial] as const,
  claimLink: (serial: string) => ["claim-link", serial] as const,
  tiers: ["tiers"] as const,
  stats: (days = 30) => ["stats", days] as const,
  audit: (limit: number) => ["audit", limit] as const,
  settings: ["settings"] as const,
};

const LIST_POLL = 30_000;
const STATS_POLL = 60_000;

// --- reads -----------------------------------------------------------------

export const useVenue = () =>
  useQuery({
    queryKey: keys.venue,
    queryFn: () => get<{ venue: Venue }>("/api/venue").then((d) => d.venue),
    staleTime: Infinity,
    // The operator host has no venue behind it.
    enabled: currentSurface() === "venue",
  });

export const PASS_PAGE = 50;

export interface PassPage {
  passes: Pass[];
  total: number;
}

/**
 * The pass list, a page at a time. The API caps a page, so the panel pages
 * too: `total` is what the venue has, the pages are what it has fetched so
 * far. Polling refetches every loaded page, fine for the few anyone scrolls.
 */
export function usePasses(filters: PassFilters = {}) {
  const params = new URLSearchParams();
  if (filters.q) params.set("q", filters.q);
  if (filters.status) params.set("status", filters.status);
  if (filters.tierId) params.set("tier_id", filters.tierId);
  return useInfiniteQuery({
    queryKey: keys.passes(filters),
    queryFn: ({ pageParam }) => {
      const qs = new URLSearchParams(params);
      qs.set("limit", String(PASS_PAGE));
      qs.set("offset", String(pageParam));
      return get<PassPage>(`/api/passes?${qs}`).then((d) => ({ passes: d.passes ?? [], total: d.total ?? 0 }));
    },
    initialPageParam: 0,
    getNextPageParam: (last, pages) => {
      const loaded = pages.reduce((n, p) => n + p.passes.length, 0);
      return loaded < last.total && last.passes.length === PASS_PAGE ? loaded : undefined;
    },
    refetchInterval: LIST_POLL,
    placeholderData: keepPreviousData,
  });
}

export const usePass = (serial: string) =>
  useQuery({
    queryKey: keys.pass(serial),
    queryFn: () => get<{ pass: Pass }>(`/api/passes/${serial}`).then((d) => d.pass),
    enabled: !!serial,
  });

export const useHistory = (serial: string) =>
  useQuery({
    queryKey: keys.history(serial),
    queryFn: () => get<ScanHistory>(`/api/passes/${serial}/history`),
    enabled: !!serial,
    refetchInterval: LIST_POLL,
  });

export const useClaimLink = (serial: string, forceNew = false) =>
  useQuery({
    queryKey: [...keys.claimLink(serial), forceNew],
    queryFn: () => get<{ claim_url: string; expires_at: string }>(`/api/passes/${serial}/claim-link${forceNew ? "?new=true" : ""}`),
    enabled: !!serial,
    staleTime: 5 * 60_000,
  });

/** Mints a fresh claim link and retires the old one; both cache slots learn it. */
export function useNewClaimLink(serial: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => get<{ claim_url: string; expires_at: string }>(`/api/passes/${serial}/claim-link?new=true`),
    onSuccess: (data) => {
      qc.setQueryData([...keys.claimLink(serial), true], data);
      qc.setQueryData([...keys.claimLink(serial), false], data);
    },
  });
}

export const useTiers = () =>
  useQuery({
    queryKey: keys.tiers,
    queryFn: () => get<{ tiers: Tier[] }>("/api/tiers").then((d) => [...(d.tiers ?? [])].sort((a, b) => a.sort_order - b.sort_order)),
    staleTime: 60_000,
  });

export const useStats = (days = 30) =>
  useQuery({
    queryKey: keys.stats(days),
    queryFn: () => get<Stats>(`/api/stats?days=${days}`),
    refetchInterval: STATS_POLL,
    placeholderData: (previous) => previous,
  });

export const useAudit = (limit = 15) =>
  useQuery({
    queryKey: keys.audit(limit),
    queryFn: () => get<{ entries: AuditEntry[] }>(`/api/audit?limit=${limit}`).then((d) => d.entries ?? []),
    refetchInterval: STATS_POLL,
    // "Cały dziennik" changes the limit; the short list stays while the long one loads.
    placeholderData: keepPreviousData,
  });

export const useSettings = () =>
  useQuery({
    queryKey: keys.settings,
    queryFn: () => get<ScannerSettings>("/api/settings/scanner"),
  });

// --- writes ----------------------------------------------------------------

const invalidatePasses = (qc: QueryClient) =>
  Promise.all([qc.invalidateQueries({ queryKey: ["passes"] }), qc.invalidateQueries({ queryKey: ["stats"] }), qc.invalidateQueries({ queryKey: keys.tiers })]);

export interface IssueInput {
  tier_key: string;
  guest_name?: string;
  max_entries?: number;
  reset_hour?: number;
  expires_at?: string;
  note?: string;
}

export function useIssuePass() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: IssueInput) => post<{ pass: Pass; claim_url: string }>("/api/passes", input),
    onSuccess: (data) => {
      // The issue response already carries the live link; seed the dialog's query.
      qc.setQueryData([...keys.claimLink(data.pass.serial), false], { claim_url: data.claim_url, expires_at: "" });
      void invalidatePasses(qc);
    },
  });
}

export interface BulkIssued {
  count: number;
  passes: { serial: string; short_id: string; claim_url: string }[];
}

export function useBulkIssue() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { tier_key: string; count: number; expires_at?: string }) => post<BulkIssued>("/api/passes/bulk", input),
    onSuccess: () => void invalidatePasses(qc),
  });
}

/** Revoke/restore with an optimistic status flip on every cached list and the detail. */
export function useSetPassStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ serial, action }: { serial: string; action: "revoke" | "restore" }) => post<{ pass: Pass }>(`/api/passes/${serial}/${action}`),
    onMutate: async ({ serial, action }) => {
      await qc.cancelQueries({ queryKey: ["passes"] });
      // A restored card goes back to where it was: in a wallet if it was ever
      // claimed, otherwise still waiting to be. Guessing "claimed" for an
      // unclaimed card flashed the wrong badge until the server answered.
      const next = (p: Pass): PassStatus => (action === "revoke" ? "revoked" : p.claimed_at ? "claimed" : "created");
      qc.setQueriesData<InfiniteData<PassPage>>({ queryKey: ["passes"] }, (data) =>
        data
          ? { ...data, pages: data.pages.map((page) => ({ ...page, passes: page.passes.map((p) => (p.serial === serial ? { ...p, status: next(p) } : p)) })) }
          : data,
      );
      qc.setQueryData<Pass>(keys.pass(serial), (p) => (p ? { ...p, status: next(p) } : p));
    },
    onSettled: (data) => {
      if (data) qc.setQueryData(keys.pass(data.pass.serial), data.pass);
      void invalidatePasses(qc);
    },
  });
}

export interface RulesPatch {
  guest_name?: string | null;
  visible?: boolean;
  max_entries?: number | null;
  reset_hour?: number | null;
  expires_at?: string | null;
  note?: string;
}

export function useUpdateRules(serial: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: RulesPatch) => patch<{ pass: Pass }>(`/api/passes/${serial}`, body),
    onSuccess: (data) => {
      qc.setQueryData(keys.pass(serial), data.pass);
      void invalidatePasses(qc);
    },
  });
}

/** Panel correction of a card's counter: signed entries plus the reason. */
export function useAdjustEntries(serial: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { delta: number; reason: string }) => post<{ pass: Pass }>(`/api/passes/${serial}/entries`, body),
    onSuccess: (data) => {
      qc.setQueryData(keys.pass(serial), data.pass);
      void qc.invalidateQueries({ queryKey: keys.history(serial) });
      void qc.invalidateQueries({ queryKey: ["audit"] });
      void invalidatePasses(qc);
    },
  });
}

export interface TierInput {
  label: string;
  perk: string;
  member_label: string;
  colors: TierColors;
  rewards: { threshold: number; label: string }[];
  reward_window_days: number | null;
  show_visits: boolean;
  show_progress: boolean;
}

export function useSaveTier() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string | null; input: TierInput }) => (id ? patch<{ tier: Tier }>(`/api/tiers/${id}`, input) : post<{ tier: Tier }>("/api/tiers", input)),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.tiers });
      void qc.invalidateQueries({ queryKey: ["passes"] });
    },
  });
}

export function useToggleTier() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (tier: Tier) => patch<{ tier: Tier }>(`/api/tiers/${tier.id}`, { visible: !tier.visible }),
    onMutate: async (tier) => {
      await qc.cancelQueries({ queryKey: keys.tiers });
      qc.setQueryData<Tier[]>(keys.tiers, (list) => list?.map((t) => (t.id === tier.id ? { ...t, visible: !t.visible } : t)));
    },
    onSettled: () => void qc.invalidateQueries({ queryKey: keys.tiers }),
  });
}

export function useDeleteTier() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => del(`/api/tiers/${id}`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.tiers }),
  });
}

/** The venue logo: upload replaces, remove goes back to the name in text. */
export function useSetVenueLogo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => upload<{ venue: Venue }>("/api/settings/logo", "image", file),
    onSuccess: (data) => qc.setQueryData(keys.venue, data.venue),
  });
}

export function useRemoveVenueLogo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => del<{ venue: Venue }>("/api/settings/logo"),
    onSuccess: (data) => qc.setQueryData(keys.venue, data.venue),
  });
}

/** Banner artwork for a level: upload replaces, remove falls back to the gradient. */
export function useSetTierArt() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, file }: { id: string; file: File }) => upload<{ tier: Tier }>(`/api/tiers/${id}/art`, "image", file),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.tiers }),
  });
}

export function useRemoveTierArt() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => del<{ tier: Tier }>(`/api/tiers/${id}/art`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.tiers }),
  });
}

/** Persists a new order; only positions that moved hit the API. Optimistic. */
export function useReorderTiers() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (ordered: Tier[]) => {
      const changes = ordered.map((tier, i) => ({ tier, position: i + 1 })).filter(({ tier, position }) => tier.sort_order !== position);
      await Promise.all(changes.map(({ tier, position }) => patch(`/api/tiers/${tier.id}`, { sort_order: position })));
    },
    onMutate: async (ordered) => {
      await qc.cancelQueries({ queryKey: keys.tiers });
      qc.setQueryData<Tier[]>(
        keys.tiers,
        ordered.map((t, i) => ({ ...t, sort_order: i + 1 })),
      );
    },
    onSettled: () => void qc.invalidateQueries({ queryKey: keys.tiers }),
  });
}

export function useSetPin(kind: "door" | "bar") {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (pin: string) => post<{ ok: boolean }>(kind === "door" ? "/api/settings/scanner-pin" : "/api/settings/claim-pin", { pin }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.settings }),
  });
}

/** Front-of-card field toggles; optimistic, since the switch should not flicker. */
export function useSetCardFields() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (fields: CardFields) => post<{ card_fields: CardFields }>("/api/settings/card-fields", fields),
    onMutate: async (fields) => {
      await qc.cancelQueries({ queryKey: keys.settings });
      qc.setQueryData<ScannerSettings>(keys.settings, (s) => (s ? { ...s, card_fields: fields } : s));
    },
    onSettled: () => void qc.invalidateQueries({ queryKey: keys.settings }),
  });
}

export function useSetAnnouncement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (message: string) => post<{ ok: boolean }>("/api/settings/announcement", { message }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.settings }),
  });
}

/** Sets a new password for the logged-in user; other sessions are logged out server-side. */
export const useChangePassword = () =>
  useMutation({
    mutationFn: (input: { current_password: string; new_password: string }) => post<{ ok: boolean }>(currentSurface() === "operator" ? "/api/operator/me/password" : "/api/me/password", input),
  });
